import { useMemo } from "react";
import type { TaktBoardResponse } from "../../../models";
import type {
  BarData,
  FlatRow,
  PackedSlotMap,
  SectionInfo,
  SectionZoneMap,
  ZoneSubgroup,
} from "../types";
import type { ZoomConfig } from "../constants";
import { MIN_BODY_ROWS } from "../constants";
import type { TaktAttribute } from "../filtering/attributes";
import { binPackEntries, getSectionCodes } from "../utils";

/**
 * Computes the packed slots, zone grouping, section info, and flat row model
 * used by TaktGrid for rendering. All pure layout computation — no side effects.
 */
export function useTaktLayout(
  data: TaktBoardResponse | null,
  zoom: ZoomConfig,
  attributes: TaktAttribute[],
) {
  const packedSlots: PackedSlotMap = useMemo(() => {
    if (!data) return new Map();
    const map: PackedSlotMap = new Map();
    for (let si = 0; si < data.sections.length; si++) {
      const section = data.sections[si];
      for (let gi = 0; gi < section.groups.length; gi++) {
        map.set(`${si}-${gi}`, binPackEntries(section.groups[gi].entries));
      }
    }
    return map;
  }, [data]);

  const sectionZones: SectionZoneMap = useMemo(() => {
    if (!data) return new Map();
    const map: SectionZoneMap = new Map();
    for (let si = 0; si < data.sections.length; si++) {
      const section = data.sections[si];
      const zoneMap = new Map<string, ZoneSubgroup>();
      const zoneOrder: string[] = [];
      for (let gi = 0; gi < section.groups.length; gi++) {
        const group = section.groups[gi];
        const key = group.takt_b;
        let zone = zoneMap.get(key);
        if (!zone) {
          zone = { takt_b: key, groups: [] };
          zoneMap.set(key, zone);
          zoneOrder.push(key);
        }
        zone.groups.push({ originalGi: gi, group });
      }
      map.set(si, zoneOrder.map((k) => zoneMap.get(k)!));
    }
    return map;
  }, [data]);

  const sectionInfos: SectionInfo[] = useMemo(() => {
    if (!data) return [];
    return data.sections.map((section) => ({
      name: section.name,
      codes: getSectionCodes(section),
    }));
  }, [data]);

  const flatRows: FlatRow[] = useMemo(() => {
    if (!data) return [];

    // Build workday index map for bar computation
    const wdIdx = new Map<string, number>();
    data.workdays.forEach((d, i) => wdIdx.set(d, i));

    // Resolve each bar's trade once here (rather than per-render in the grid),
    // reusing the generic "trade" attribute as the single task_code→trade join.
    const tradeAttr = attributes.find((a) => a.key === "trade");
    const tradeById = new Map(tradeAttr?.domain.map((d) => [d.id, d]));

    const rows: FlatRow[] = [];

    for (let si = 0; si < data.sections.length; si++) {
      const section = data.sections[si];
      const zones = sectionZones.get(si) || [];
      const codes = getSectionCodes(section);

      // Count actual body rows and pixel height for this section
      let bodyRowCount = 0;
      let bodyPixelHeight = 0;
      for (const zone of zones) {
        bodyRowCount += 1; // zone header
        bodyPixelHeight += zoom.zoneHeaderTotalH;
        for (const { originalGi } of zone.groups) {
          const slots = packedSlots.get(`${si}-${originalGi}`) || [];
          bodyRowCount += slots.length;
          bodyPixelHeight += slots.length * zoom.dataRowTotalH;
        }
      }

      // Spacers for short sections
      const spacersNeeded = Math.max(0, MIN_BODY_ROWS - bodyRowCount);
      bodyPixelHeight += spacersNeeded * zoom.spacerRowH;

      // Legend spans from section header through all body rows
      const legendHeight = zoom.sectionRowTotalH + bodyPixelHeight;

      // Section header
      rows.push({
        type: "section-header",
        si,
        section,
        codes,
        bodyRowCount: Math.max(bodyRowCount, MIN_BODY_ROWS),
        legendHeight,
      });

      // Zone headers + data rows
      for (const zone of zones) {
        rows.push({ type: "zone-header", si, takt_b: zone.takt_b });

        for (const { originalGi, group } of zone.groups) {
          const slots = packedSlots.get(`${si}-${originalGi}`) || [];
          slots.forEach((slot, slotIdx) => {
            // Compute bar positions from entries
            const bars: BarData[] = [];
            for (const entry of slot) {
              let sI = wdIdx.get(entry.start);
              let fI = wdIdx.get(entry.finish);

              // Clamp to nearest workday if exact date not in workday list
              if (sI === undefined) {
                for (let i = 0; i < data.workdays.length; i++) {
                  if (data.workdays[i] >= entry.start) { sI = i; break; }
                }
                if (sI === undefined) sI = data.workdays.length - 1;
              }
              if (fI === undefined) {
                for (let i = data.workdays.length - 1; i >= 0; i--) {
                  if (data.workdays[i] <= entry.finish) { fI = i; break; }
                }
                if (fI === undefined) fI = 0;
              }

              const tradeId = tradeAttr?.entryValues(entry)[0];
              const trade = tradeId ? tradeById.get(tradeId) : undefined;

              bars.push({
                left: sI * zoom.colWidth,
                width: (fI - sI + 1) * zoom.colWidth,
                workCode: entry.work_code,
                label: entry.label,
                status: entry.status,
                tradeName: trade?.label || "",
                tradeFill: trade?.fill || "",
                tradeFont: trade?.font || "",
              });
            }

            rows.push({
              type: "data-row",
              rowKey: `${si}-${originalGi}-${slotIdx}`,
              sectionName: section.name,
              taktB: zone.takt_b,
              taktC: group.takt_c,
              si,
              gi: originalGi,
              slotIdx,
              isFirstInGroup: slotIdx === 0,
              slot,
              bars,
            });
          });
        }
      }

      // Pad short sections to minimum row count for legend readability
      for (let s = 0; s < spacersNeeded; s++) {
        rows.push({ type: "spacer", si });
      }
    }

    return rows;
  }, [data, sectionZones, packedSlots, zoom, attributes]);

  return {
    sectionInfos,
    flatRows,
  };
}
